// src/sentry.ts
import * as Sentry from "@sentry/react";

const env = import.meta.env;
let isInitialized = false;

/* -------------------------- helpers -------------------------- */

function getPathnameFromUrl(u: string | URL) {
  try {
    const url = typeof u === "string" ? new URL(u, window.location.href) : u;
    return url.pathname;
  } catch {
    return typeof u === "string" ? u : "[unparseable-url]";
  }
}

function bestEffortCallsite(): string | undefined {
  // Try to capture the first app frame (helps when multiple subsequent calls fire)
  const err = new Error("__stack_probe__");
  const stack = (err.stack || "").split("\n").slice(1);
  const appFrame = stack.find(
    (l) =>
      !/\/node_modules\//.test(l) &&
      !/@sentry\//.test(l) &&
      !/monitorSlowApi|sentry\.ts/.test(l)
  );
  return appFrame?.trim();
}

function sanitizeRequestBody(body: unknown): unknown {
  if (!body) return null;

  try {
    let parsed: unknown;
    if (typeof body === "string") {
      parsed = JSON.parse(body);
    } else if (body instanceof FormData) {
      const formObj: Record<string, string> = {};
      body.forEach((value, key) => {
        formObj[key] =
          typeof value === "string" ? "[FormData String]" : "[FormData File]";
      });
      return formObj;
    } else {
      parsed = body;
    }

    // Sanitize sensitive fields
    const sensitiveFields = [
      "password",
      "token",
      "secret",
      "key",
      "auth",
      "credential",
      "ssn",
      "social",
      "credit",
      "card",
      "cvv",
      "pin",
      "otp",
      "email",
      "phone",
      "address",
      "dob",
      "birthdate",
    ];

    const sanitize = (obj: unknown): unknown => {
      if (typeof obj !== "object" || obj === null) return obj;

      if (Array.isArray(obj)) {
        return obj.map((item) => sanitize(item));
      }

      const sanitized: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(
        obj as Record<string, unknown>
      )) {
        const lowerKey = key.toLowerCase();
        const isSensitive = sensitiveFields.some((field) =>
          lowerKey.includes(field)
        );

        if (isSensitive) {
          sanitized[key] = "[REDACTED]";
        } else if (typeof value === "object" && value !== null) {
          sanitized[key] = sanitize(value);
        } else {
          sanitized[key] = value;
        }
      }

      return sanitized;
    };

    return sanitize(parsed);
  } catch {
    return "[Unparseable Body]";
  }
}

function getErrorCategory(status: number): string {
  if (status >= 400 && status < 500) return "client_error";
  if (status >= 500) return "server_error";
  return "unknown_error";
}

function shouldReportApiFailure(status: number, url: string): boolean {
  // Don't report 404s and 401s
  if (status === 404 || status === 401) return false;

  // Don't report client-side aborts or network errors without status
  if (!status) return false;

  // Don't report for health check endpoints
  if (url.includes("/health") || url.includes("/ping")) return false;

  // Report all other 4xx and 5xx errors
  return status >= 400;
}

function attachApiMonitoring(
  fetchImpl: typeof fetch,
  slowThresholdMs = 15_000,
  abortSlow = false
) {
  const wrapped: typeof fetch = async (input, init) => {
    const start = performance.now();
    const initialPath = (() => {
      if (input instanceof Request) return getPathnameFromUrl(input.url);
      if (input instanceof URL) return getPathnameFromUrl(input);
      return getPathnameFromUrl(input as string);
    })();

    // Extract full URL for better context
    const fullUrl = (() => {
      if (input instanceof Request) return input.url;
      if (input instanceof URL) return input.toString();
      if (typeof input === "string") {
        try {
          return new URL(input, window.location.href).toString();
        } catch {
          return input;
        }
      }
      return "[unknown-url]";
    })();

    // track route + callsite at capture time
    const route = window.location.pathname;
    const callsite = bestEffortCallsite();

    // Optional deadline abort
    let timer: number | undefined;
    let controller: AbortController | undefined;
    let finalInit = init;

    if (abortSlow) {
      controller = new AbortController();
      timer = window.setTimeout(
        () => controller?.abort("deadline_exceeded"),
        slowThresholdMs
      );
      finalInit = { ...init, signal: controller.signal };
    }

    const method =
      init?.method ?? (input instanceof Request ? input.method : "GET");

    // Capture request details for error reporting
    const requestBody =
      init?.body || (input instanceof Request ? input.body : null);
    const sanitizedBody = sanitizeRequestBody(requestBody);
    const requestHeaders =
      init?.headers ||
      (input instanceof Request
        ? Object.fromEntries(input.headers.entries())
        : {});

    let status: number | undefined;
    let ok: boolean | undefined;
    let finalPath = initialPath;

    try {
      const res = await fetchImpl(input, finalInit);

      // Use the *final* URL reported by the browser (correct endpoint if redirected or constructed)
      if (res && typeof res.url === "string" && res.url) {
        finalPath = getPathnameFromUrl(res.url);
      }

      status = res.status;
      ok = res.ok;

      // Report API failures (non-404 errors)
      if (shouldReportApiFailure(status, finalPath)) {
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        // Try to get response body for error context (but don't consume the stream)
        let errorResponseBody: unknown = null;
        try {
          const clonedResponse = res.clone();
          const text = await clonedResponse.text();
          if (text) {
            try {
              errorResponseBody = JSON.parse(text);
            } catch {
              errorResponseBody = text.substring(0, 500); // Limit size
            }
          }
        } catch {
          // Ignore response body extraction errors
        }

        const errorCategory = getErrorCategory(status);
        const duration = performance.now() - start;

        Sentry.captureException(
          new Error(`API ${errorCategory}: ${status} ${method} ${finalPath}`),
          {
            level: status >= 500 ? "error" : "warning",
            tags: {
              type: "api_failure",
              method,
              status_code: status.toString(),
              url_path: finalPath,
              route,
              error_category: errorCategory,
              is_server_error: (status >= 500).toString(),
              is_client_error: (status >= 400 && status < 500).toString(),
            },
            extra: {
              duration_ms: Math.round(duration),
              full_url: fullUrl,
              initial_path: initialPath,
              callsite,
              request_body: sanitizedBody,
              request_headers: requestHeaders,
              response_body: errorResponseBody,
              user_agent: navigator.userAgent,
              timestamp: new Date().toISOString(),
            },
            fingerprint: ["api_failure", method, finalPath, status.toString()],
          }
        );
      }

      return res;
    } catch (err: unknown) {
      const took = performance.now() - start;
      const error = err as Error;
      const isDeadline =
        error?.name === "AbortError" || error?.message === "deadline_exceeded";

      // Handle network errors and timeouts
      if (isDeadline && took >= slowThresholdMs) {
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        Sentry.captureException(new Error("API deadline exceeded (>10s)"), {
          level: "error",
          tags: {
            type: "slow_api",
            method,
            url_path: finalPath,
            route,
            deadline: `${slowThresholdMs}ms`,
            aborted: "true",
          },
          extra: {
            duration_ms: Math.round(took),
            callsite,
            initial_path: initialPath,
            full_url: fullUrl,
            request_body: sanitizedBody,
            request_headers: requestHeaders,
          },
          fingerprint: ["slow_api", method, finalPath, "deadline"],
        });
      } else if (!isDeadline) {
        // Report network errors, fetch failures, etc.
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        Sentry.captureException(error, {
          level: "error",
          tags: {
            type: "api_network_error",
            method,
            url_path: finalPath,
            route,
            error_name: error?.name || "unknown",
          },
          extra: {
            duration_ms: Math.round(took),
            callsite,
            initial_path: initialPath,
            full_url: fullUrl,
            request_body: sanitizedBody,
            request_headers: requestHeaders,
            error_message: error?.message,
            error_stack: error?.stack,
          },
          fingerprint: [
            "api_network_error",
            method,
            finalPath,
            error?.name || "unknown",
          ],
        });
      }

      throw err;
    } finally {
      if (timer) clearTimeout(timer);
      const duration = performance.now() - start;

      // Report slow APIs (successful but slow)
      if (!abortSlow && duration > slowThresholdMs && ok) {
        const facilityId =
          window.localStorage.getItem("selectedClinic") ?? "unknown";
        Sentry.setTag("facilityId", facilityId);

        Sentry.captureMessage("Slow API response (>10s)", {
          level: "warning",
          tags: {
            type: "slow_api",
            method,
            url_path: finalPath,
            route,
            status_code: status?.toString() || "unknown",
          },
          extra: {
            duration_ms: Math.round(duration),
            status,
            ok,
            callsite,
            initial_path: initialPath,
            full_url: fullUrl,
          },
          fingerprint: ["slow_api", method, finalPath],
        });
      }
    }
  };

  (window as typeof globalThis).fetch = wrapped;
}

/* -------------------------- public API -------------------------- */

// Set user context in Sentry
export const setSentryUser = (userId: string, email: string) => {
  Sentry.setUser({ id: userId, email });
};

export const setSentryExtraContext = (facilityId: string) => {
  Sentry.setExtra("facilityId", facilityId);
};

// Manually report API errors (for cases where fetch wrapper doesn't catch them)
export const reportApiError = (
  error: Error,
  context: {
    method?: string;
    url?: string;
    status?: number;
    requestBody?: unknown;
    responseBody?: unknown;
    duration?: number;
  }
) => {
  const facilityId = window.localStorage.getItem("selectedClinic") ?? "unknown";
  Sentry.setTag("facilityId", facilityId);

  const errorCategory = context.status
    ? getErrorCategory(context.status)
    : "network_error";
  const urlPath = context.url ? getPathnameFromUrl(context.url) : "unknown";

  // Network errors (CORS, timeout, etc.) should be treated as errors, not warnings
  const level = context.status
    ? context.status >= 500
      ? "error"
      : "warning"
    : "error"; // Network errors are always "error" level

  Sentry.captureException(error, {
    level,
    tags: {
      type: "manual_api_error",
      method: context.method || "unknown",
      status_code: context.status?.toString() || "unknown",
      url_path: urlPath,
      route: window.location.pathname,
      error_category: errorCategory,
      is_network_error: (!context.status).toString(),
      is_cors_error: (
        error.message.includes("CORS") ||
        error.message.includes("Access-Control")
      ).toString(),
    },
    extra: {
      duration_ms: context.duration ? Math.round(context.duration) : undefined,
      full_url: context.url,
      callsite: bestEffortCallsite(),
      request_body: context.requestBody
        ? sanitizeRequestBody(context.requestBody)
        : null,
      response_body: context.responseBody,
      user_email: window.localStorage.getItem("userEmail"),
      facilityId: facilityId,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
    fingerprint: [
      "manual_api_error",
      context.method || "unknown",
      urlPath,
      context.status?.toString() || "unknown",
    ],
  });
};

// Report custom application errors with context
export const reportAppError = (
  error: Error,
  context: {
    component?: string;
    action?: string;
    userId?: string;
    additionalData?: Record<string, unknown>;
  } = {}
) => {
  const facilityId = window.localStorage.getItem("selectedClinic") ?? "unknown";
  Sentry.setTag("facilityId", facilityId);

  Sentry.captureException(error, {
    level: "error",
    tags: {
      type: "app_error",
      component: context.component || "unknown",
      action: context.action || "unknown",
      route: window.location.pathname,
    },
    extra: {
      callsite: bestEffortCallsite(),
      user_id: window.localStorage.getItem("userEmail"),
      additional_data: context.additionalData,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    },
    fingerprint: [
      "app_error",
      context.component || "unknown",
      context.action || "unknown",
    ],
  });
};

export const initializeSentry = () => {
  if (isInitialized) return;

  const enabled = ![
    "localhost",
    "cipstaging.circadia.link",
    "cip-beta.circadia.link",
    "cipbeta.circadia.link",
  ].includes(window.location.hostname);

  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    enabled,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        maskAttributes: [
          "password",
          "social-security",
          "phone",
          "email",
          "name",
          "address",
          "city",
          "state",
          "zip",
          "country",
        ],
        beforeAddRecordingEvent: (event) => {
          // exclude console logs
          if ("category" in event && event.category === "console") return null;
          return event;
        },
        beforeErrorSampling: () => true,
      }),
    ],
    replaysSessionSampleRate: 0.1, // 10% of normal sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    tracesSampleRate: 0.2, // 20% of traces
    profilesSampleRate: 0.8, // 80% of profiles (adjust if too noisy)
    sendDefaultPii: true,
    tracePropagationTargets: [
      "localhost",
      "https://cip.circadia.link",
      "https://cipstaging.circadia.link",
      "https://cipdev.circadia.link",
      "https://app.circadia.health",
    ],
    environment:
      window.location.hostname === "localhost"
        ? "local"
        : window.location.hostname.includes("staging")
        ? "staging"
        : "production",
  });

  isInitialized = true;

  attachApiMonitoring(window.fetch, 15_000, /* abortSlow */ false);

  // Seed user/facility context from local storage if available
  const userId = window.localStorage.getItem("userEmail");
  const userEmail = window.localStorage.getItem("userEmail");
  if (userId && userEmail) setSentryUser(userId, userEmail);

  const facilityId = window.localStorage.getItem("selectedClinic");
  if (facilityId) setSentryExtraContext(facilityId);
};

// Initialize immediately for apps that import this module once at bootstrap
initializeSentry();

export const SentryErrorBoundary = Sentry.ErrorBoundary;
